import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import {
  Menu,
  X,
} from "lucide-react";
import { useLanguage } from "../providers/LanguageProvider";

const MobileMenu = ({ menuItems, contactInfo, socialLinks }) => {
  const { toggleLanguage, translations, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);


  return (
    <div className="lg:hidden">
      {/* Language Toggle Button */}
      <button
        className="fixed top-3 end-3 z-50 bg-gray-300/10 backdrop-blur-sm w-10 h-10 rounded-sm transition-all duration-300 hover:bg-white/80 flex items-center justify-center"
        onClick={toggleLanguage}
      >
        <div className="text-text-light font-medium text-sm">
          {isRTL ? ("En") : ("עב")}
        </div>
      </button>

      {/* Menu Trigger Button, should be after the buttons so they would get faded when the menu is open */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-3 start-3 z-50 bg-gray-300/10 backdrop-blur-sm w-10 h-10 rounded-sm hover:bg-white/80 transition-all duration-300 flex items-center justify-center"
      >
        <Menu className="text-text-light w-5 h-5" />
      </button>

      {/* Mobile Menu Overlay with transitions */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
          }`}
      >
        {/* Backdrop with blur */}
        <div
          className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
            }`}
          onClick={() => setIsOpen(false)}
        />

        {/* Menu Content */}
        <div
          className={`fixed inset-y-0 start-0 w-[300px] sm:w-[400px] bg-white shadow-xl overflow-y-auto transform transition-transform duration-300 ease-out ${isOpen
              ? "translate-x-0"
              : "ltr:-translate-x-full rtl:translate-x-full"
            }`}
        >
          {/* Scrollable Container */}
          <div className="min-h-full flex flex-col">
            {/* Header with sticky close button */}
            <div className="sticky top-0 bg-white z-10 px-4 py-4 flex items-center justify-between border-b border-neutral-200">
              <h2 className="text-lg font-semibold text-neutral-900">
                {translations.menu.title}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-neutral-600 hover:bg-neutral-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <nav className="py-6">
                {menuItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.link}
                    className="flex items-center px-4 py-3 text-neutral-600 hover:bg-neutral-100 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.icon}
                    <span className="ms-3">{item.title}</span>
                  </a>
                ))}
              </nav>

              <div className="border-t border-neutral-200 py-6">
                <h2 className="text-lg font-semibold text-neutral-900 mb-4 px-4">
                  {translations.menu.contact.button}
                </h2>
                {contactInfo.map((item, index) => {
                  if (item.link) {
                    return (
                      <a
                        key={index}
                        href={item.link}
                        className="flex items-center px-4 py-3 text-neutral-600 hover:bg-neutral-100 transition-colors"
                      >
                        {item.icon}
                        <span className="ms-3">{item.text}</span>
                      </a>
                    );
                  }
                  else {
                    return (
                      <div
                        key={index}
                        className="flex items-center px-4 py-2 text-neutral-600"
                      >
                        {item.icon}
                        <span className="ms-3">{item.text}</span>
                      </div>
                    );
                  }
                })}
              </div>

              <div className="border-t border-neutral-200 py-6">
                <div className="flex justify-center space-s-6">
                  {socialLinks.map((item, index) => (
                    <a
                      key={index}
                      href={item.link}
                      className="text-neutral-600 hover:text-primary-600 transition-colors"
                      aria-label={item.label}
                    >
                      {item.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


MobileMenu.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
      icon: PropTypes.element.isRequired,
    })
  ).isRequired,
  contactInfo: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element.isRequired,
      text: PropTypes.string.isRequired,
      link: PropTypes.string,
    })
  ).isRequired,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element.isRequired,
      link: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default MobileMenu;
